import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo } from 'react';
import { getFirestore, doc, onSnapshot, DocumentReference } from 'firebase/firestore';
import { User } from 'firebase/auth';
import { Config, Flags, UserProfile } from '@finvari/deliver';
import { useSubscribedDoc } from '../utils/firebaseHooks';

// Hook to get current user profile with permissions
export function useCurrentUser(firebaseUser: User | null) {
  const userProfileRef = useMemo(() => {
    if (!firebaseUser) return null;
    const db = getFirestore();
    return doc(db, 'userProfile', firebaseUser.uid) as DocumentReference<UserProfile>;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [firebaseUser?.uid]);

  const { userProfile: firestoreProfile, loading, error } = useSubscribedDoc<UserProfile, 'userProfile'>(
    userProfileRef!,
    'userProfile'
  );

  // Merge Firestore profile with Firebase user data, providing defaults
  const userProfile = useMemo((): UserProfile | null => {
    if (!firebaseUser) return null;

    // If we have a Firestore profile, use it (but ensure uid/email are set)
    if (firestoreProfile) {
      return {
        ...firestoreProfile,
        uid: firebaseUser.uid,
        email: firestoreProfile.email || firebaseUser.email || '',
      };
    }

    // Default profile when no Firestore document exists
    return {
      uid: firebaseUser.uid,
      email: firebaseUser.email ?? '',
      displayName: firebaseUser.displayName ?? undefined,
      photoURL: firebaseUser.photoURL ?? undefined,
      canDeployToProtectedEnvironments: false,
      canEditProtectedEnvironments: false,
    };
  }, [firebaseUser, firestoreProfile]);

  return {
    userProfile,
    loading: firebaseUser ? loading : false,
    error,
  };
}

interface ConfigContextType {
  config: Config | null;
  // Feature flags from config/flags. Null until loaded / when signed out. Consumers
  // treat a missing field as its safe default (e.g. `flags?.someFlag === true`).
  flags: Flags | null;
  loading: boolean;
  error: string | null;
  userProfile: UserProfile | null;
}

const ConfigContext = createContext<ConfigContextType | undefined>(undefined);

export const useConfig = () => {
  const context = useContext(ConfigContext);
  if (context === undefined) {
    throw new Error('useConfig must be used within a ConfigProvider');
  }
  return context;
};

interface ConfigProviderProps {
  children: ReactNode;
  user: User | null;
}

export const ConfigProvider: React.FC<ConfigProviderProps> = ({ children, user }) => {
  const [config, setConfig] = useState<Config | null>(null);
  const [flags, setFlags] = useState<Flags | null>(null);
  const [configLoading, setConfigLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Use the useCurrentUser hook for user profile
  const { userProfile, loading: userLoading } = useCurrentUser(user);

  // Fetch config
  useEffect(() => {
    // Only set up Firestore listener if user is authenticated
    if (!user) {
      setConfigLoading(false);
      setConfig(null);
      setFlags(null);
      setError(null);
      return;
    }

    setConfigLoading(true);
    setError(null);

    const db = getFirestore();
    const configRef = doc(db, 'config', 'main');
    const flagsRef = doc(db, 'config', 'flags');

    // Feature flags are best-effort and never block config loading: a missing doc resolves to an
    // empty object (so every flag falls back to its default), and a read error is only logged,
    // leaving flags at its previous value.
    const unsubscribeFlags = onSnapshot(
      flagsRef,
      (snap) => setFlags(snap.exists() ? (snap.data() as Flags) : {}),
      (err) => console.warn('Failed to load flags:', err),
    );

    const unsubscribe = onSnapshot(configRef, (docSnapshot) => {
      try {
        if (docSnapshot.exists()) {
          setConfig(docSnapshot.data() as Config);
          setConfigLoading(false);
        } else {
          // Config document doesn't exist yet - stay in loading state
          // Server should create it with defaults
          setConfig(null);
          // Don't set loading to false - keep waiting for config to be created
        }
      } catch (err: any) {
        console.error('Error processing config:', err);
        setError(err.message || 'Failed to process config data');
        setConfigLoading(false);
      }
    }, (err) => {
      setError(err.message || 'Failed to load config');
      setConfigLoading(false);
    });

    return () => {
      unsubscribe();
      unsubscribeFlags();
    };
  }, [user]);

  const value: ConfigContextType = {
    config,
    flags,
    loading: configLoading || userLoading,
    error,
    userProfile,
  };

  return (
    <ConfigContext.Provider value={value}>
      {children}
    </ConfigContext.Provider>
  );
};